import css from "./AudioInputPreferences.sass?inline"
import {createElement} from "@opendaw/lib-jsx"
import {Html} from "@opendaw/lib-dom"
import {DefaultObservableValue, Lifecycle, Option, StringMapping, Terminator} from "@opendaw/lib-std"
import {AudioDevices, DeviceLatency, MicSource} from "@opendaw/studio-core"
import {Colors} from "@opendaw/studio-enums"
import {StudioService} from "@/service/StudioService"
import {NumberInput} from "@/ui/components/NumberInput"
import {RadioGroup} from "@/ui/components/RadioGroup"
import {Button} from "@/ui/components/Button"
import {showAudioCalibrationModal} from "@/ui/AudioCalibrationModal"

const className = Html.adoptStyleSheet(css, "AudioInputPreferences")

type Construct = {
    lifecycle: Lifecycle
    service: StudioService
}

const empty = (element: HTMLElement) => {while (element.firstChild) {element.removeChild(element.firstChild)}}

const deviceLabel = (deviceId: string): string =>
    AudioDevices.inputs.find(device => device.deviceId === deviceId)?.label ?? "Unknown device"

export const AudioInputPreferences = ({lifecycle, service}: Construct) => {
    const {preferences} = service.engine
    const offsetModel = lifecycle.own(preferences.createMutableObservableValue("recording", "additionalInputLatencyMs"))
    const deviceList: HTMLElement = (<div className="device-list"/>)
    const latencyTable: HTMLElement = (<div className="latency-table"/>)
    const runtime = lifecycle.own(new Terminator())
    const render = () => {
        runtime.terminate()
        empty(deviceList)
        empty(latencyTable)
        const activeId = MicSource.resolveDevice()?.deviceId
        const inputs = AudioDevices.inputs
        if (inputs.length === 0) {
            AudioDevices.requestPermission().then(render)
        } else {
            for (const device of inputs) {
                deviceList.append(
                    <div className={Html.buildClassList("device", device.deviceId === activeId && "active")}
                         onclick={() => MicSource.deviceId.wrapOption(Option.wrap(device.deviceId))}>
                        {device.label}
                    </div>
                )
            }
        }
        const entries = DeviceLatency.list()
        if (entries.length === 0) {
            latencyTable.append(<p className="hint">No microphones have been calibrated yet.</p>)
        } else {
            for (const {deviceId, ms} of entries) {
                const msModel = runtime.own(new DefaultObservableValue<number>(ms))
                runtime.own(msModel.subscribe(owner => DeviceLatency.save(deviceId, owner.getValue())))
                latencyTable.append(
                    <div className={Html.buildClassList("row", deviceId === activeId && "active")}>
                        <span className="name" title={deviceLabel(deviceId)}>{deviceLabel(deviceId)}</span>
                        <NumberInput lifecycle={runtime} model={msModel}
                                     mapper={StringMapping.numeric({unit: "ms"})} maxChars={4}/>
                        <Button lifecycle={runtime} appearance={{color: Colors.gray}}
                                onClick={() => {DeviceLatency.clear(deviceId); render()}}>
                            <span>Clear</span>
                        </Button>
                    </div>
                )
            }
        }
    }
    lifecycle.own(MicSource.deviceId.catchupAndSubscribe(render))
    return (
        <div className={className}>
            <h3>Microphone</h3>
            <p className="hint">A single microphone is used for all recording.</p>
            {deviceList}
            <div className="field">
                <label>Channels</label>
                <RadioGroup lifecycle={lifecycle}
                            model={MicSource.recordChannels}
                            elements={[
                                {value: 2, element: <span>Stereo</span>},
                                {value: 1, element: <span>Mono</span>}
                            ]}
                            appearance={{color: Colors.black, activeColor: Colors.bright, cursor: "pointer"}}/>
            </div>
            <div className="field">
                <label>Record gain</label>
                <NumberInput lifecycle={lifecycle}
                             model={MicSource.recordGainDb}
                             mapper={StringMapping.numeric({unit: "dB", fractionDigits: 1})}
                             maxChars={5}/>
            </div>
            <h3>Input latency per microphone</h3>
            <p className="hint">Total input latency = calibrated per-mic latency + the additional offset below.</p>
            {latencyTable}
            <div className="field">
                <Button lifecycle={lifecycle} appearance={{color: Colors.blue}}
                        onClick={() => showAudioCalibrationModal(service.project.env.audioContext, undefined, render)}>
                    <span>Calibrate active microphone</span>
                </Button>
            </div>
            <div className="field">
                <label>Additional input latency (ms)</label>
                <NumberInput lifecycle={lifecycle} model={offsetModel}
                             mapper={StringMapping.numeric({unit: "ms"})} maxChars={4}/>
            </div>
        </div>
    )
}
